import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Grid } from "@mui/material";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axios from "axios";
import moment from "moment";
import { LocaleContext } from "../../routing/LangRouter";

//import redux store
import { saveItineraryList } from "../../store/actions";

//import utils
import { formatPersianDate, generateDateRangeArray } from "../../utils/date";

//import constants
import BASE_URL from "../../constants/baseUrls";
import { IS_MOBILE } from "../../constants/breakpoints";

//import general components
import Loading from "../../components/common/loading/loading";
import WithoutDestinationModal from "../../components/common/without-modal/without-destination-modal";
import LoginModal from "../../components/common/login-modal/login-modal";

//import view components
import ItineraryList from "../../components/view/itinerary/itinerary-list/itinerary-list";
import MobileItineraryList from "../../components/view/itinerary/mobile-view/itinerary-list";
import ItineraryTime from "../../components/view/itinerary/itinerary-time/itinerary-time";
import Rating from "../../components/view/itinerary/rating/rating";
import WithoutDestinationDateModal from "../../components/common/without-modal/without-destination-date-modal";
import WithoutDateModal from "../../components/common/without-modal/without-date-modal";

//import images and icons
import { Airplane, Calendar, User, Warning2 } from "iconsax-react";

//import styles
import "./itinerary.scss";
import EmptyOrErrorState from "../../components/common/empty-or-error-state/empty-or-error-state";

function Itinerary({ dispatch, data }) {
    //general variables
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const accessToken = localStorage.getItem("accessToken");

    //sessionStorage variables
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));
    const selectedFlight = JSON.parse(sessionStorage.getItem("selectedFlight"));
    const selectedIntFlight = JSON.parse(sessionStorage.getItem("selectedIntFlight"));
    const selectedHotel = JSON.parse(sessionStorage.getItem("selectedHotel"));
    const selectedRooms = JSON.parse(sessionStorage.getItem("selectedRooms"));
    const selectedIntRooms = JSON.parse(sessionStorage.getItem("selectedIntRooms"));

    //itinerary request
    const [requestFlag, setRequestFlag] = useState(itineraryInformation.destination === null ? false : itineraryInformation.itineraryGenerate);

    //location
    const [destinationCoordinates, setDestinationCoordinates] = useState(null);
    useEffect(() => {
        if (selectedHotel) {
            setDestinationCoordinates(selectedHotel?.location);
        } else if (itineraryInformation.destination !== null) {
            setDestinationCoordinates(itineraryInformation?.destination?.latitude + "," + itineraryInformation?.destination?.longitude);
        }
    }, [selectedHotel, requestFlag]);

    //temperatures request
    const [temperaturesValue, setTemperaturesValue] = useState([]);
    const getTemperatures = async () => {
        const query = `latitude=${destinationCoordinates?.split(",")[0]}&longitude=${destinationCoordinates?.split(",")[1]}`;
        const api = {
            base: "https://api.open-meteo.com/v1/forecast/",
        };
        try {
            setLoading(true);
            const response = await axios.get(
                `${api.base}?${query}&hourly=temperature_2m,weathercode&timezone=Asia/Tehran&start_date=${itineraryInformation?.departureDate}&end_date=${itineraryInformation?.returnDate}`,
            );
            const temperatureList = response.data.hourly.temperature_2m.map(temp => {
                return temp ? temp : 12;
            });
            setTemperaturesValue(temperatureList);
        } catch (error) {
            setTemperaturesValue([]);
        } finally {
            getItinerary();
        }
    };

    //itinerary request
    const [isError, setIsError] = useState(false);
    const getItinerary = async () => {
        const favourites = JSON.parse(sessionStorage?.getItem("favorites"));
        Object.keys(favourites).forEach(item => {
            favourites[item] = parseInt(favourites[item]);
        });
        const startDatetime = itineraryInformation?.departureDate + "T" + itineraryInformation?.firstDayTime;
        const endDatetime = itineraryInformation?.returnDate + "T" + itineraryInformation?.lastDayTime;

        const params = JSON.stringify({
            location: destinationCoordinates,
            temperatures: temperaturesValue,

            favourites,
            timings: {
                start_datetime: startDatetime,
                end_datetime: endDatetime,
                custom_start_time: itineraryInformation?.startTime,
                custom_end_time: itineraryInformation?.endTime,
                min_lunch_start_time: "13:00:00",
                max_lunch_start_time: "14:30:00",
                min_dinner_start_time: "20:00:00",
                max_dinner_start_time: "21:30:00",
                meal_time_duration: 6000,
            },
            edit_data: {
                plan_id: "",
                removed_loc_global_id: "",
                all_visited_locs: [],
                today_visited_locs: [],
            },
            travelers: {
                adults_count: itineraryInformation?.personCounter.adult,
                children_count: itineraryInformation?.personCounter.child,
                infants_count: itineraryInformation?.personCounter.infant,
            },
            user_id: accessToken ? JSON.parse(localStorage.getItem("userInformation")).id : 0,
        });

        try {
            setIsError(false);
            const response = await axios.post(`${BASE_URL}/itinerary/plan/${itineraryInformation?.destination?.id}/`, params, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });
            dispatch(saveItineraryList(response.data));
            if (!selectedFlight && !selectedIntFlight && !selectedHotel && (!selectedRooms || !selectedIntRooms) && !!accessToken) {
                savePlan(response.data?.[0]?.[0]?.plan_id);
            }
        } catch (error) {
            // toast.error(t("errorMessage"), { toastId: "custom-id-yes" });
            setIsError(true);
        } finally {
            setLoading(false);
            setRequestFlag(false);
            itineraryInformation.itineraryGenerate = false;
            sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
        }
    };

    const savePlan = async id => {
        await axios.post(
            `${BASE_URL}/itinerary/save_plan/`,
            { plan: id },
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    };

    useEffect(() => {
        if (
            itineraryInformation.destination !== null &&
            requestFlag &&
            destinationCoordinates &&
            !!itineraryInformation.departureDate &&
            !!itineraryInformation.returnDate
        ) {
            getTemperatures();
        }
    }, [destinationCoordinates, requestFlag, itineraryInformation.departureDate]);

    //handle times changes
    const handleTimeChange = (firstDayTime, lastDayTime, startTime, endTime) => {
        itineraryInformation.itineraryGenerate = true;
        itineraryInformation.firstDayTime = firstDayTime.id;
        itineraryInformation.lastDayTime = lastDayTime.id;
        itineraryInformation.startTime = startTime.id;
        itineraryInformation.endTime = endTime.id;
        sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
        setRequestFlag(true);

        if (IS_MOBILE) {
            setOpenTimes(false);
            searchParams.delete("action");
            setSearchParams(searchParams);
        }
    };

    //get plan id
    const [planId, setPlanId] = useState("");
    useEffect(() => {
        if (data?.length) {
            const itinrary = data?.find(itinrary => itinrary?.length > 0);
            const item = itinrary?.find(item => item);
            setPlanId(item?.plan_id);
        }
    }, [data]);

    //request to confirm itinrary
    const handleConfirmItinrary = e => {
        sessionStorage.setItem("planId", JSON.stringify(planId));
        sessionStorage.setItem("startDatetime", JSON.stringify(itineraryInformation?.departureDate + "T" + itineraryInformation?.firstDayTime));
        sessionStorage.setItem("endDatetime", JSON.stringify(itineraryInformation?.returnDate + "T" + itineraryInformation?.lastDayTime));
        if (!!selectedFlight || !!selectedIntFlight || (!!selectedHotel && (!!selectedRooms || !!selectedIntRooms))) {
            navigate(`/${locale}/cart`);
        }
    };

    //handle modal change
    // const [openLogin, setOpenLogin] = useState(false);
    const [openTimes, setOpenTimes] = useState(false);
    const [openWithoutDate, setOpenWithoutDate] = useState(false);
    const [openWithoutDestination, setOpenWithoutDestination] = useState(false);
    const [openWithoutDestinationDate, setOpenWithoutDestinationDate] = useState(false);
    useEffect(() => {
        // if (searchParams.get("action") === "login") setOpenLogin(true);
        // else setOpenLogin(false);

        if (IS_MOBILE) {
            if (searchParams.get("action") === "set-time") setOpenTimes(true);
            else setOpenTimes(false);
        }

        if (itineraryInformation?.destination !== null && itineraryInformation?.departureDate === null && itineraryInformation?.returnDate === null)
            setOpenWithoutDate(true);
        else setOpenWithoutDate(false);

        if (itineraryInformation?.destination === null && !!itineraryInformation?.departureDate && !!itineraryInformation?.returnDate)
            setOpenWithoutDestination(true);
        else setOpenWithoutDestination(false);

        if (itineraryInformation?.destination === null && itineraryInformation?.departureDate === null && itineraryInformation?.returnDate === null)
            setOpenWithoutDestinationDate(true);
        else setOpenWithoutDestinationDate(false);
    }, [searchParams]);

    //rating
    const [rangeValue, setRangeValue] = useState({ taste_matching: 0, variety: 0, location_distances: 0 });
    const changeRange = (value, name) => {
        setRangeValue(values => ({ ...values, [name]: value }));
    };
    const submitRating = async () => {
        const data = {
            plan_id: planId,
            taste_matching: rangeValue.taste_matching,
            location_distances: rangeValue.location_distances,
            variety: rangeValue.variety,
        };
        try {
            await axios.post(`${BASE_URL}/api/v2/account/user/plan-rate/`, data, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            searchParams.delete("action");
            setSearchParams(searchParams);
            getRating();
            toast.success(t("successMessage"), { toastId: "custom-id-yes" });
        } catch (error) {
            if (error.response.status === 401) {
                localStorage.clear();
                setSearchParams({ action: "login" });
            } else {
                toast.error(t("errorMessage"), { toastId: "custom-id-yes" });
            }
        }
    };
    const getRating = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/api/v2/account/user/plan-rate/`, {
                params: {
                    plan_id: planId,
                },
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            setRangeValue(response.data.data);
        } catch (error) {
            if (error.response.status === 401) {
                localStorage.clear();
                setSearchParams({ action: "login" });
            } else {
                // toast.error("درخواست با خطا مواجه شد", { toastId: "custom-id-yes" });
            }
        }
    };
    useEffect(() => {
        if (planId && !!accessToken && searchParams.get("action") === "rating") {
            getRating();
        }
    }, [planId, accessToken]);

    //format date in Persian format
    const formatDate = date => {
        return formatPersianDate(date, {
            day: "numeric",
            month: "long",
            year: undefined,
            weekday: "long",
        });
    };

    return (
        <Grid className="itinerary" container justifyContent="center">
            {loading ? <Loading text={t("itineraryLoadingMessage")} size="large" /> : ""}

            <Grid className="dddd" container item xs={12} rowGap={{ xs: 0, md: 5 }} justifyContent="center">
                <Grid container item xs={12}>
                    <Grid
                        className="itinerary__travel-information"
                        container
                        item
                        xs={12}
                        columnGap={5}
                        display={{ xs: "none", md: "flex" }}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <span>
                            <Airplane size="24" color="var(--gray-color)" variant="Bold" />
                            {locale === "en" ? itineraryInformation?.departure?.english_name : itineraryInformation?.departure?.name} {t("to")}{" "}
                            {locale === "en" ? itineraryInformation?.destination?.english_name : itineraryInformation?.destination?.name}{" "}
                        </span>
                        <span>
                            <Calendar size="24" color="var(--gray-color)" variant="Bold" />
                            {locale === "en"
                                ? moment(itineraryInformation.departureDate).format("ddd D MMM")
                                : formatDate(itineraryInformation.departureDate)}{" "}
                            {t("to-2")}{" "}
                            {locale === "en"
                                ? moment(itineraryInformation.returnDate).format("ddd D MMM")
                                : formatDate(itineraryInformation.returnDate)}{" "}
                        </span>
                        <span>
                            <User color="var(--gray-color)" variant="Bold" />
                            {itineraryInformation.personCounter.totalPersons} {t("person")}
                        </span>
                    </Grid>
                    <ItineraryTime open={openTimes} setOpen={setOpenTimes} handleTimeChange={handleTimeChange} />
                </Grid>

                {isError ? (
                    <Grid container item xs={11}>
                        <EmptyOrErrorState
                            title={t("errorMessage")}
                            handleClick={getItinerary}
                            actionLabel={t("retry")}
                            icon={<Warning2 size="32" color="var(--red-color)" variant="Bold" />}
                        />{" "}
                    </Grid>
                ) : IS_MOBILE ? (
                    <MobileItineraryList
                        destinationCoordinates={destinationCoordinates}
                        allDates={generateDateRangeArray(itineraryInformation?.departureDate, itineraryInformation?.returnDate)}
                        handleConfirmItinrary={handleConfirmItinrary}
                        isError={isError}
                    />
                ) : (
                    <ItineraryList
                        destinationCoordinates={destinationCoordinates}
                        allDates={generateDateRangeArray(itineraryInformation?.departureDate, itineraryInformation?.returnDate)}
                        handleConfirmItinrary={handleConfirmItinrary}
                        isError={isError}
                    />
                )}
            </Grid>

            <Rating min={0} max={5} value={rangeValue} step={1} onChange={changeRange} active={true} submitForm={submitRating} />
            {/* <LoginModal open={openLogin} setOpen={setOpenLogin} /> */}
            <WithoutDateModal open={openWithoutDate} setOpen={setOpenWithoutDate} setRequestFlag={setRequestFlag} />
            <WithoutDestinationModal open={openWithoutDestination} setOpen={setOpenWithoutDestination} setRequestFlag={setRequestFlag} />
            <WithoutDestinationDateModal open={openWithoutDestinationDate} setOpen={setOpenWithoutDestinationDate} setRequestFlag={setRequestFlag} />
        </Grid>
    );
}

function mapStateToProps(state) {
    return {
        data: state.itineraryList,
    };
}

export default connect(mapStateToProps)(Itinerary);
